// Visual Query Builder — pure SQL generator (one-way: model → SQL).
//
// No DOM, no async: takes a builder MODEL and returns { sql, warnings } so it's
// node-testable in isolation (same ethos as lib/erlayout.js + lib/sqlformat.js).
// The panel owns the model (a Solid store) and feeds it here on every change to
// produce the live preview. We do NOT parse SQL back into the model (v1).
//
// MODEL shape:
//   {
//     tables:  [{ name, alias }],            // tables[0] is the FROM root; order matters
//     joins:   [{ left:{table,col}, right:{table,col}, type:'INNER'|'LEFT'|'RIGHT' }],
//     columns: [{ table, column, alias, agg, show, sort, group }],
//                // agg:  '' | 'COUNT' | 'COUNT_DISTINCT' | 'SUM' | 'AVG' | 'MIN' | 'MAX'
//                // sort: '' | 'ASC' | 'DESC'
//                // show: bool (appears in SELECT)   group: bool (appears in GROUP BY)
//     filters: <group>,                      // { op:'AND'|'OR', items:[ <cond> | <group> ] }
//                // cond: { table, column, op, value, value2 }
//     distinct: bool,
//     limit:    number | null,
//   }
//
// Identifiers are always backtick-quoted; tables are aliased (t1, t2, …) so every
// column reference qualifies cleanly even across self-joins.

// Backtick-quote one identifier (escape embedded backticks per MySQL rules).
export const qident = (s) => '`' + String(s ?? '').replace(/`/g, '``') + '`';

// Aggregate operators offered in the UI (value → label).
export const AGGREGATES = [
  { v: '', label: '(none)' },
  { v: 'COUNT', label: 'COUNT' },
  { v: 'COUNT_DISTINCT', label: 'COUNT DISTINCT' },
  { v: 'SUM', label: 'SUM' },
  { v: 'AVG', label: 'AVG' },
  { v: 'MIN', label: 'MIN' },
  { v: 'MAX', label: 'MAX' },
];

// Comparison operators offered in the filter builder. `arity` tells the UI how
// many value inputs to show (0 = none, 1 = single, 2 = range, 'list' = csv).
export const OPERATORS = [
  { v: '=', label: '=', arity: 1 },
  { v: '<>', label: '≠', arity: 1 },
  { v: '<', label: '<', arity: 1 },
  { v: '<=', label: '≤', arity: 1 },
  { v: '>', label: '>', arity: 1 },
  { v: '>=', label: '≥', arity: 1 },
  { v: 'LIKE', label: 'LIKE', arity: 1 },
  { v: 'NOT LIKE', label: 'NOT LIKE', arity: 1 },
  { v: 'IN', label: 'IN', arity: 'list' },
  { v: 'NOT IN', label: 'NOT IN', arity: 'list' },
  { v: 'BETWEEN', label: 'BETWEEN', arity: 2 },
  { v: 'IS NULL', label: 'IS NULL', arity: 0 },
  { v: 'IS NOT NULL', label: 'IS NOT NULL', arity: 0 },
];

const arityOf = (op) => (OPERATORS.find((o) => o.v === op) || { arity: 1 }).arity;

// Single-quoted MySQL string literal. Backslash is MySQL's escape character
// (NO_BACKSLASH_ESCAPES isn't assumed), so it's doubled along with the quote —
// else `C:\temp\` swallows the closing quote and `x\' OR 1=1` escapes it.
export const qstring = (s) => "'" + String(s)
  .replace(/\\/g, '\\\\').replace(/'/g, "''")
  .replace(/\0/g, '\\0').replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\x1a/g, '\\Z') + "'";

// A complete single-quoted literal written into the value box, e.g. '007' or
// 'NULL'. Only a well-formed one passes through verbatim: every inner quote is
// doubled or backslash-escaped, so it can't close early.
const QUOTED_LITERAL = /^'(?:[^'\\]|\\[\s\S]|'')*'$/;

// Render a scalar literal. Numbers and a few keywords pass through raw; anything
// else is a single-quoted string. A leading '@' (user var) or a value wrapped in
// backticks is treated as raw so power users can reference columns / @params
// from the value box, and a value already written as a quoted literal ('007')
// stays a string — that's how a numeric-looking or keyword string is typed.
export const renderValue = (raw) => {
  const v = String(raw ?? '').trim();
  if (v === '') return "''";
  if (/^-?\d+(\.\d+)?$/.test(v)) return v;                 // numeric literal
  if (/^(null|true|false)$/i.test(v)) return v.toUpperCase();
  if (/^@[a-zA-Z0-9_]+$/.test(v)) return v;                // @user_var
  if (v.length >= 2 && v.startsWith('`') && v.endsWith('`')) return v; // raw ident
  if (QUOTED_LITERAL.test(v)) return v;                    // already a string literal
  return qstring(v);
};

// valueText is renderValue's inverse, for the parser: the value-box text that
// renders back to the string literal `s` — the bare text when renderValue would
// quote it unchanged, else the quoted literal (so '007' stays a string instead
// of becoming the number 7, 'a,b' stays one IN item, and '' isn't read as an
// empty — skipped — value).
export const valueText = (s, inList = false) =>
  s !== '' && renderValue(s) === qstring(s) && !(inList && /[\n,]/.test(s)) ? s : qstring(s);

// Split a comma/newline-separated list for IN (…), quoting each element. An item
// that starts with a quoted literal ('a,b') keeps the separators inside it; a
// quote anywhere else is plain text (O'Brien).
const splitList = (src) => {
  const parts = [];
  let i = 0;
  while (i <= src.length) {
    let j = i;
    while (j < src.length && /[ \t\r]/.test(src[j])) j++;
    if (src[j] === "'") {
      for (j++; j < src.length; j++) {
        if (src[j] === '\\') { j++; continue; }
        if (src[j] === "'") { if (src[j + 1] === "'") { j++; continue; } j++; break; }
      }
    }
    while (j < src.length && src[j] !== ',' && src[j] !== '\n') j++;
    parts.push(src.slice(i, j).trim());
    i = j + 1;
  }
  return parts.filter((s) => s !== '');
};

const renderList = (raw) => {
  const parts = splitList(String(raw ?? ''));
  return parts.length ? parts.map(renderValue).join(', ') : "''";
};

// Qualified column reference: `alias`.`col` using the alias map.
const colRef = (aliasOf, table, column) => `${qident(aliasOf(table))}.${qident(column)}`;

// SELECT/ORDER expression for a column entry (applies its aggregate, if any).
const exprOf = (aliasOf, c) => {
  const ref = colRef(aliasOf, c.table, c.column);
  switch (c.agg) {
    case 'COUNT': return `COUNT(${ref})`;
    case 'COUNT_DISTINCT': return `COUNT(DISTINCT ${ref})`;
    case 'SUM': return `SUM(${ref})`;
    case 'AVG': return `AVG(${ref})`;
    case 'MIN': return `MIN(${ref})`;
    case 'MAX': return `MAX(${ref})`;
    default: return ref;
  }
};

// Render a single filter condition. Returns '' for an incomplete row (skipped).
const renderCond = (aliasOf, cond) => {
  if (!cond || !cond.table || !cond.column || !cond.op) return '';
  const ref = colRef(aliasOf, cond.table, cond.column);
  const a = arityOf(cond.op);
  if (a === 0) return `${ref} ${cond.op}`;
  if (a === 'list') return `${ref} ${cond.op} (${renderList(cond.value)})`;
  if (a === 2) {
    if (String(cond.value ?? '') === '' || String(cond.value2 ?? '') === '') return '';
    return `${ref} BETWEEN ${renderValue(cond.value)} AND ${renderValue(cond.value2)}`;
  }
  if (String(cond.value ?? '') === '') return '';
  return `${ref} ${cond.op} ${renderValue(cond.value)}`;
};

// Recursively render a filter group → a parenthesised AND/OR expression.
// Empty / all-incomplete groups render to '' (clause omitted).
const renderGroup = (aliasOf, group) => {
  if (!group || !Array.isArray(group.items) || group.items.length === 0) return '';
  const op = group.op === 'OR' ? 'OR' : 'AND';
  const parts = group.items
    .map((it) => (it && it.items ? renderGroup(aliasOf, it) : renderCond(aliasOf, it)))
    .filter((s) => s !== '');
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return '(' + parts.join(` ${op} `) + ')';
};

// Assemble FROM + JOINs. Greedy: start from tables[0], repeatedly attach any
// not-yet-included table that has a join to an already-included one (ANDing all
// such joins into its ON). Tables with no connecting join become CROSS JOINs and
// raise a warning. Returns { clause, warnings }.
const buildFrom = (model, aliasOf) => {
  const warnings = [];
  const tables = model.tables || [];
  if (tables.length === 0) return { clause: '', warnings };

  const tableTerm = (t) => `${qident(t.name)} ${qident(t.alias)}`;
  const root = tables[0];
  let clause = tableTerm(root);
  const included = new Set([root.name]);
  const remaining = tables.slice(1);
  const joins = model.joins || [];

  let progress = true;
  while (remaining.length && progress) {
    progress = false;
    for (let i = 0; i < remaining.length; i++) {
      const t = remaining[i];
      // All joins linking this table to an already-included table.
      const linking = joins.filter((j) => {
        const a = j.left.table, b = j.right.table;
        return (a === t.name && included.has(b)) || (b === t.name && included.has(a));
      });
      if (linking.length === 0) continue;
      // Join type: prefer a LEFT/RIGHT if any link declares one, else INNER.
      const type = linking.find((j) => j.type && j.type !== 'INNER')?.type || 'INNER';
      const on = linking
        .map((j) => `${colRef(aliasOf, j.left.table, j.left.col)} = ${colRef(aliasOf, j.right.table, j.right.col)}`)
        .join(' AND ');
      clause += ` ${type} JOIN ${tableTerm(t)} ON ${on}`;
      included.add(t.name);
      remaining.splice(i, 1);
      progress = true;
      break;
    }
  }
  // Anything still unattached has no join path — CROSS JOIN + warn.
  for (const t of remaining) {
    clause += ` CROSS JOIN ${tableTerm(t)}`;
    warnings.push(`Table "${t.name}" has no join to the rest of the query — added as CROSS JOIN (cartesian product).`);
    included.add(t.name);
  }
  return { clause, warnings };
};

// buildSQL: the entry point. model → { sql, warnings }. `sql` is single-line
// (the panel runs it through formatSql for the pretty preview).
export function buildSQL(model) {
  const warnings = [];
  const tables = model?.tables || [];
  if (tables.length === 0) return { sql: '', warnings: ['Add a table to start.'] };

  const aliasMap = new Map(tables.map((t) => [t.name, t.alias]));
  const aliasOf = (name) => aliasMap.get(name) || name;

  const cols = (model.columns || []).filter((c) => c.show);
  const hasAgg = (model.columns || []).some((c) => c.agg);

  // SELECT list.
  let selectList;
  if (cols.length === 0) {
    selectList = '*';
  } else {
    selectList = cols
      .map((c) => {
        const e = exprOf(aliasOf, c);
        return c.alias ? `${e} AS ${qident(c.alias)}` : e;
      })
      .join(', ');
  }

  let sql = 'SELECT ';
  if (model.distinct) sql += 'DISTINCT ';
  sql += selectList;

  const from = buildFrom(model, aliasOf);
  warnings.push(...from.warnings);
  sql += ` FROM ${from.clause}`;

  const where = renderGroup(aliasOf, model.filters);
  if (where) sql += ` WHERE ${where.startsWith('(') ? where.slice(1, -1) : where}`;

  // GROUP BY: explicit per-column flags. If aggregates are present but nothing is
  // flagged, warn (the query may be invalid under ONLY_FULL_GROUP_BY).
  const groupCols = (model.columns || []).filter((c) => c.group && !c.agg);
  if (groupCols.length) {
    sql += ` GROUP BY ${groupCols.map((c) => colRef(aliasOf, c.table, c.column)).join(', ')}`;
  } else if (hasAgg && cols.some((c) => !c.agg)) {
    warnings.push('Aggregates are mixed with plain columns but no GROUP BY is set — mark the plain columns as "Group".');
  }

  // ORDER BY: columns with a sort direction, in column order.
  const sortCols = (model.columns || []).filter((c) => c.sort === 'ASC' || c.sort === 'DESC');
  if (sortCols.length) {
    sql += ` ORDER BY ${sortCols.map((c) => `${exprOf(aliasOf, c)} ${c.sort}`).join(', ')}`;
  }

  if (model.limit != null && model.limit !== '' && Number(model.limit) > 0) {
    sql += ` LIMIT ${Math.floor(Number(model.limit))}`;
  }

  return { sql: sql + ';', warnings };
}

// Next free table alias (t1, t2, …) given the tables already on the canvas.
export function nextAlias(tables) {
  const used = new Set((tables || []).map((t) => t.alias));
  for (let i = 1; ; i++) {
    const a = `t${i}`;
    if (!used.has(a)) return a;
  }
}
